import * as React from "react";
import { useNavigate } from "react-router-dom";

import { useSurface } from "@/contexts/surface-context";
import {
  useSubscriptionState,
  isSubscriptionLapsed,
  type SubscriptionState,
} from "@/hooks/queries/useSubscriptionState";
import { startEvaluationDraftNow } from "@/lib/draft-producer-runner";
import { isSurfaceAllowed } from "@/lib/surface-override";
import type { Surface } from "@/lib/surface-detect";

// The "New evaluation" create flow (Story 6.9, Tasks 4 / AC 10-11-13). Centralises the
// two gates + the create so every call-site (the clients-list "New eval" chip is the
// only real create site; the home/evals header buttons just route to the picker) shares
// one implementation:
//
//   1. Subscription gate (AC 13) — a lapsed subscription disables the write CTA; the
//      hook also no-ops defensively if invoked while lapsed.
//   2. Surface gate (AC 11; enforcing per gh-469) — on a disallowed iOS surface the flow
//      surfaces the Install Gate INSTEAD of creating a draft and creates NOTHING. There
//      is no "Continue anyway" escape: the only forward path is installing the PWA, which
//      re-classifies the surface and clears the gate.
//   3. Create (AC 10) — `startEvaluationDraftNow({ clientId })` (Story 7.8, offline-first,
//      client-minted id) → navigate to `/evaluations/{id}` (opens setup).

export interface StartEvaluationFlow {
  /** Attempt to start a draft for `clientId`: runs the gates, then creates + navigates. */
  startEvaluation: (clientId: string) => void;
  /** True when the subscription is lapsed — call-sites disable their CTA (AC 13). */
  lapsed: boolean;
  /**
   * The resolved subscription state the gate derived `lapsed` from — exposed so
   * call-sites can pick a source-aware blocked-write reason (gh-568: an org-seat
   * clinician is told to contact their administrator, never to self-renew).
   */
  subscription: SubscriptionState;
  /** True while a create request is in flight (disable the CTA, show a busy label). */
  isCreating: boolean;
  /** Set when an offline-first create unexpectedly fails — the call-site can surface it. */
  error: string | null;
  /**
   * Non-null ⇒ the create was attempted on a disallowed surface: the call-site renders
   * `<InstallGate surface={gateSurface} />` in place of its content until the clinician
   * installs the PWA. Enforcing (gh-469) — there is no escape; nothing is created.
   */
  gateSurface: Surface | null;
}

export function useStartEvaluation(): StartEvaluationFlow {
  const navigate = useNavigate();
  const { surface } = useSurface();
  const subscription = useSubscriptionState();
  const lapsed = isSubscriptionLapsed(subscription);

  const [gateSurface, setGateSurface] = React.useState<Surface | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const doCreate = React.useCallback(
    async (clientId: string) => {
      setIsCreating(true);
      setError(null);
      try {
        // Offline-first (Story 7.8): the id is minted on-device, so this resolves even
        // while offline — navigation to the stepper always succeeds.
        const { id } = await startEvaluationDraftNow({ clientId });
        navigate(`/evaluations/${id}`);
      } catch {
        setError("Couldn't start a new evaluation. Please try again.");
      } finally {
        setIsCreating(false);
      }
    },
    [navigate],
  );

  const startEvaluation = React.useCallback(
    (clientId: string) => {
      // Defensive: the CTA is disabled when lapsed, but never create on a stray call.
      if (lapsed) return;
      if (!isSurfaceAllowed(surface)) {
        // Raise the enforcing gate instead of creating (AC 11; gh-469 — no escape hatch,
        // nothing is created; the only forward path is installing the PWA).
        setGateSurface(surface);
        return;
      }
      void doCreate(clientId);
    },
    [lapsed, surface, doCreate],
  );

  return { startEvaluation, lapsed, subscription, isCreating, error, gateSurface };
}
